import { ChangeEvent, useCallback, useLayoutEffect, useRef } from "react";

const isDigit = (char: string) => char >= "0" && char <= "9";

/**
 * Keeps the caret in place while a controlled input rewrites its own value.
 *
 * Re-rendering a masked field with a reformatted string parks the caret at the
 * end, so correcting a digit in the middle is impossible — every keystroke
 * jumps you back to the end. This counts the digits before the caret, then
 * restores the caret to whichever spot in the formatted value has the same
 * number of digits in front of it.
 *
 * Returns a change handler that yields the formatted value to store in state.
 */
export const useMaskedInput = (
  format: (input: string, previous: string) => string,
) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const caretRef = useRef<number | null>(null);

  // Runs after the reformatted value is committed — i.e. after the browser has
  // already moved the caret to the end — so this puts it back.
  useLayoutEffect(() => {
    const input = inputRef.current;
    const caret = caretRef.current;

    inputRef.current = null;
    caretRef.current = null;

    if (input && caret !== null) {
      input.setSelectionRange(caret, caret);
    }
  });

  return useCallback(
    (event: ChangeEvent<HTMLInputElement>, previous: string) => {
      const input = event.target;
      const rawCaret = input.selectionStart ?? input.value.length;
      const digitsBeforeCaret = Array.from(
        input.value.slice(0, rawCaret),
      ).filter(isDigit).length;

      const formatted = format(input.value, previous);

      // Walk the formatted value until the same number of digits sits behind us.
      let caret = 0;
      let seen = 0;
      while (caret < formatted.length && seen < digitsBeforeCaret) {
        if (isDigit(formatted[caret])) {
          seen++;
        }
        caret++;
      }

      // Only mask characters left after the caret means the user is typing
      // forward, so skip past them rather than stranding them behind " RT ".
      if (!Array.from(formatted.slice(caret)).some(isDigit)) {
        caret = formatted.length;
      }

      inputRef.current = input;
      caretRef.current = caret;

      return formatted;
    },
    [format],
  );
};
